import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner, Form } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function ClientRetention() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Current Metrics
  const [hasCustomers, setHasCustomers] = useState('');
  const [retentionRate, setRetentionRate] = useState('');
  const [averageLifetime, setAverageLifetime] = useState('');
  const [churnReasons, setChurnReasons] = useState('');

  // Onboarding
  const [hasOnboarding, setHasOnboarding] = useState('');
  const [onboardingProcess, setOnboardingProcess] = useState('');

  // Support & Success
  const [supportChannels, setSupportChannels] = useState([]);
  const [responseTime, setResponseTime] = useState('');
  const [hasSuccessManager, setHasSuccessManager] = useState('');

  // Engagement
  const [engagementTactics, setEngagementTactics] = useState([]);
  const [communicationFrequency, setCommunicationFrequency] = useState('');

  // Growth Opportunities
  const [hasUpsells, setHasUpsells] = useState('');
  const [upsellStrategy, setUpsellStrategy] = useState('');
  const [hasCommunity, setHasCommunity] = useState('');

  const supportChannelOptions = ['Email', 'Phone', 'Live Chat', 'Help Center/Docs', 'Social Media', 'In-App Support'];
  const engagementOptions = ['Regular Check-ins', 'Educational Content', 'Product Updates', 'Success Stories', 'Exclusive Events', 'Loyalty Program'];

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    await fetchSavedRetention();
    setLoading(false);
  };

  const fetchSavedRetention = async () => {
    try {
      const response = await fetch('/api/marketing/get-retention', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.retention) {
          const r = data.retention;
          setHasCustomers(r.has_customers || '');
          setRetentionRate(r.retention_rate || '');
          setAverageLifetime(r.average_lifetime || '');
          setChurnReasons(r.churn_reasons || '');
          setHasOnboarding(r.has_onboarding || '');
          setOnboardingProcess(r.onboarding_process || '');
          setSupportChannels(r.support_channels || []);
          setResponseTime(r.response_time || '');
          setHasSuccessManager(r.has_success_manager || '');
          setEngagementTactics(r.engagement_tactics || []);
          setCommunicationFrequency(r.communication_frequency || '');
          setHasUpsells(r.has_upsells || '');
          setUpsellStrategy(r.upsell_strategy || '');
          setHasCommunity(r.has_community || '');
        }
      }
    } catch (err) {
      console.log('No saved retention strategy found');
    }
  };

  const handleSupportChannelToggle = (channel) => {
    if (supportChannels.includes(channel)) {
      setSupportChannels(supportChannels.filter(c => c !== channel));
    } else {
      setSupportChannels([...supportChannels, channel]);
    }
  };

  const handleEngagementToggle = (tactic) => {
    if (engagementTactics.includes(tactic)) {
      setEngagementTactics(engagementTactics.filter(t => t !== tactic));
    } else {
      setEngagementTactics([...engagementTactics, tactic]);
    }
  };

  const handleSaveAndContinue = async () => {
    // Basic validation
    if (!hasCustomers) {
      setError('Please indicate if you have existing customers');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!hasOnboarding) {
      setError('Please indicate if you have an onboarding process');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (supportChannels.length === 0) {
      setError('Please select at least one support channel');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/marketing/save-retention', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          retention: {
            has_customers: hasCustomers,
            retention_rate: retentionRate,
            average_lifetime: averageLifetime,
            churn_reasons: churnReasons,
            has_onboarding: hasOnboarding,
            onboarding_process: onboardingProcess,
            support_channels: supportChannels,
            response_time: responseTime,
            has_success_manager: hasSuccessManager,
            engagement_tactics: engagementTactics,
            communication_frequency: communicationFrequency,
            has_upsells: hasUpsells,
            upsell_strategy: upsellStrategy,
            has_community: hasCommunity
          }
        })
      });

      if (response.ok) {
        navigate('/dashboard/marketing-strategy');
      } else {
        const errorData = await response.json();
        console.error('Save error:', errorData);
        setError(errorData.error || 'Failed to save retention strategy');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Network error:', err);
      setError('Error saving retention strategy: ' + err.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="marketing-strategy" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <h1 className="text-center mb-3">Client Retention Strategy</h1>
              <p className="text-center text-muted mb-4">
                Build systems to keep customers happy, engaged, and coming back for more.
              </p>

              {error && <Alert variant="danger">{error}</Alert>}

              {/* Section 1: Current Retention Metrics */}
              <div className="mb-5">
                <h3 className="mb-3">Current Retention Metrics</h3>

                <Form.Group className="mb-3">
                  <Form.Label>Do you currently have paying customers?</Form.Label>
                  <Form.Select
                    value={hasCustomers}
                    onChange={(e) => setHasCustomers(e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes, I have customers</option>
                    <option value="no">No, pre-launch/no customers yet</option>
                  </Form.Select>
                </Form.Group>

                {hasCustomers === 'yes' && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>What's your approximate customer retention rate?</Form.Label>
                      <Form.Select
                        value={retentionRate}
                        onChange={(e) => setRetentionRate(e.target.value)}
                      >
                        <option value="">Select retention rate...</option>
                        <option value="90%+">90%+ (excellent)</option>
                        <option value="70-90%">70-90% (good)</option>
                        <option value="50-70%">50-70% (needs work)</option>
                        <option value="below-50%">Below 50% (critical issue)</option>
                        <option value="unknown">I don't know</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Percentage of customers who remain customers after 6-12 months
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Average customer lifetime (optional)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g., 18 months or 3 years"
                        value={averageLifetime}
                        onChange={(e) => setAverageLifetime(e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        How long do customers typically stay with you?
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Why do customers typically leave? (if known)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="e.g., Price concerns, not seeing results fast enough, found competitor, poor support experience"
                        value={churnReasons}
                        onChange={(e) => setChurnReasons(e.target.value)}
                      />
                    </Form.Group>
                  </>
                )}
              </div>

              {/* Section 2: Onboarding Process */}
              <div className="mb-5">
                <h3 className="mb-3">Onboarding Process</h3>
                <p className="text-muted mb-3">
                  First impressions matter. A strong onboarding process dramatically improves retention.
                </p>

                <Form.Group className="mb-3">
                  <Form.Label>Do you have a structured onboarding process?</Form.Label>
                  <Form.Select
                    value={hasOnboarding}
                    onChange={(e) => setHasOnboarding(e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="yes-detailed">Yes, detailed multi-step process</option>
                    <option value="yes-basic">Yes, basic welcome email/setup</option>
                    <option value="no">No formal onboarding process</option>
                  </Form.Select>
                </Form.Group>

                {(hasOnboarding === 'yes-detailed' || hasOnboarding === 'yes-basic') && (
                  <Form.Group className="mb-3">
                    <Form.Label>Describe your onboarding process</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      placeholder="e.g., Welcome email → Setup call → Video tutorial series → 30-day check-in → First success milestone celebration"
                      value={onboardingProcess}
                      onChange={(e) => setOnboardingProcess(e.target.value)}
                    />
                  </Form.Group>
                )}
              </div>

              {/* Section 3: Support & Success */}
              <div className="mb-5">
                <h3 className="mb-3">Customer Support & Success</h3>

                <div className="mb-3">
                  <Form.Label>What support channels do you offer?</Form.Label>
                  {supportChannelOptions.map(channel => (
                    <Form.Check
                      key={channel}
                      type="checkbox"
                      label={channel}
                      checked={supportChannels.includes(channel)}
                      onChange={() => handleSupportChannelToggle(channel)}
                    />
                  ))}
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Average support response time</Form.Label>
                  <Form.Select
                    value={responseTime}
                    onChange={(e) => setResponseTime(e.target.value)}
                  >
                    <option value="">Select response time...</option>
                    <option value="under-1-hour">Under 1 hour</option>
                    <option value="1-4-hours">1-4 hours</option>
                    <option value="4-24-hours">4-24 hours</option>
                    <option value="1-3-days">1-3 days</option>
                    <option value="over-3-days">Over 3 days</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Do you have dedicated customer success managers?</Form.Label>
                  <Form.Select
                    value={hasSuccessManager}
                    onChange={(e) => setHasSuccessManager(e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="yes-all">Yes, for all customers</option>
                    <option value="yes-premium">Yes, for premium customers only</option>
                    <option value="no">No, general support only</option>
                    <option value="planned">Planned for the future</option>
                  </Form.Select>
                </Form.Group>
              </div>

              {/* Section 4: Engagement & Communication */}
              <div className="mb-5">
                <h3 className="mb-3">Ongoing Engagement</h3>

                <div className="mb-3">
                  <Form.Label>How do you keep customers engaged?</Form.Label>
                  {engagementOptions.map(tactic => (
                    <Form.Check
                      key={tactic}
                      type="checkbox"
                      label={tactic}
                      checked={engagementTactics.includes(tactic)}
                      onChange={() => handleEngagementToggle(tactic)}
                    />
                  ))}
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>How often do you communicate with customers?</Form.Label>
                  <Form.Select
                    value={communicationFrequency}
                    onChange={(e) => setCommunicationFrequency(e.target.value)}
                  >
                    <option value="">Select frequency...</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="bi-weekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="rarely">Rarely/only when needed</option>
                  </Form.Select>
                </Form.Group>
              </div>

              {/* Section 5: Growth & Expansion */}
              <div className="mb-4">
                <h3 className="mb-3">Upsells & Community</h3>

                <Form.Group className="mb-3">
                  <Form.Label>Do you have upsell or cross-sell opportunities?</Form.Label>
                  <Form.Select
                    value={hasUpsells}
                    onChange={(e) => setHasUpsells(e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="yes-active">Yes, actively promoting</option>
                    <option value="yes-available">Yes, available but not promoted</option>
                    <option value="planned">Planned but not built yet</option>
                    <option value="no">No upsell opportunities</option>
                  </Form.Select>
                </Form.Group>

                {(hasUpsells === 'yes-active' || hasUpsells === 'yes-available') && (
                  <Form.Group className="mb-3">
                    <Form.Label>Describe your upsell/expansion strategy</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="e.g., After 3 months, offer premium tier with more features. Quarterly business reviews to identify expansion needs."
                      value={upsellStrategy}
                      onChange={(e) => setUpsellStrategy(e.target.value)}
                    />
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Do you have a community or loyalty program?</Form.Label>
                  <Form.Select
                    value={hasCommunity}
                    onChange={(e) => setHasCommunity(e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="yes-active">Yes, active community</option>
                    <option value="yes-small">Yes, small/growing community</option>
                    <option value="planned">Planning to build one</option>
                    <option value="no">No community</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Communities create stickiness and reduce churn significantly
                  </Form.Text>
                </Form.Group>
              </div>

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/dashboard/marketing-strategy')}
                >
                  ← Back to Dashboard
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  disabled={saving}
                  onClick={handleSaveAndContinue}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    'Save & Continue →'
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default ClientRetention;
